import { Icon, IconButton, Input } from "@chakra-ui/react";
import { IoMdCheckmark } from "react-icons/io";
import { BiTransfer } from "react-icons/bi";
import { MdClose } from "react-icons/md";
import React, { useEffect, useState } from "react";
import { FiEdit2 } from "react-icons/fi";
import Modal, { SimpleModalControls } from "../../../components/Modal";
import { useTranslation } from "react-i18next";
import EditBillModal from "../../Bills/EditBillModal";
import { toast } from "react-toastify";
import { getAxiosInstance } from "../../../utils/axiosInstance";

type MainAction = "ACCEPT" | "DECLINE" | "CANCEL";

interface Entry {
  from: string;
  to: string;
  product: string;
  quantity: number;
  um: string;
  createdAt: string;
  createdBy: string;
  cancelType: MainAction;
  cancelAction: () => void;
  accept?: MainAction;
  acceptAction?: () => void;
  canEdit?: boolean;
  id: string;
  reload?: () => void;
}

const ActionButton = (props: { type: MainAction, action: () => void }) => {
  const getIconByType = (type: MainAction) => {
    switch (type) {
      case "ACCEPT":
        return <IoMdCheckmark />
      case "CANCEL":
        return <MdClose />
      case "DECLINE":
        return <MdClose />
      default:
        return <IoMdCheckmark />
    }
  }

  const getColorByType = (type: MainAction) => {
    switch (type) {
      case "ACCEPT":
        return 'teal';
      case "CANCEL":
        return "red";
      case "DECLINE":
        return "red";
      default:
        return 'teal';
    }
  }

  return (
    <IconButton
      colorScheme={getColorByType(props.type)}
      aria-label='Action'
      fontSize='30px'
      rounded={15}
      icon={getIconByType(props.type)}
      onClick={props.action}
    />
  )
}

const EntryUpdateModal = (props: SimpleModalControls & { id: string; quantity: number; title: string; }) => {
  const {
    isOpen,
    onClose,
    quantity,
    id,
    title
  } = props;
  
  const [q, setQ] = useState<number>(quantity);
  const { t } = useTranslation("translations");

  const update = async () => {
    try {
      await getAxiosInstance().put("/Transfer/"+id, { quantity: q });
      toast.success(t("SUCCESSFULLY_UPDATE_TRANSFER"), { position: "bottom-right"});
      onClose();
    } catch (e) {
      toast.error(t(""), { position: "bottom-right" });
    }
  }

  useEffect(() => {
    setQ(quantity);
  }, [])

  return (
    <Modal
      header={title}
      isOpen={isOpen}
      onClose={onClose}    
      secondaryActionEnabled={q !== quantity}
      secondaryAction={() => update()}
      secondaryActionName={t("SAVE")}
    >
        <Input
          value={q}
          placeholder={"QUANTITY"}
          onChange={(e) => setQ(Math.max(parseInt(e.target.value), 0))}
        />
    </Modal>
  )
}

const Entry = (props: Entry) => {
  const {
    from,
    to,
    product,
    quantity,
    um,
    cancelType,
    cancelAction,
    accept,
    acceptAction,
    createdAt,
    createdBy,
    canEdit,
    id,
    reload
  } = props;

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  return <div className="flex flex-col md:flex-row w-100 min-h-[30px] text-md font-bold rounded-md bg-amber-100 p-2 m-2 justify-between items-center">
    <div className="w-[100%] md:w-2/4 flex flex-col md:flex-row justify-between items-center">
      <div>
        {from}
      </div>
      <div>
        <Icon
          as={BiTransfer}
          color="green.500"
          h={8}
          w={8}
        />
      </div>
      <div>
        {to}
      </div>
    </div>

    <div className="w-[100%] m-4 md:m-0 md:w-1/6 flex flex-col md:flex-row items-center md:justify-around md:justify-between">
      <div className="my-2 md:my-0">
        {product}
      </div>
      <div className="my-2 md:my-0">
        {quantity} {um}
      </div>
    </div>
    <div className="w-[100%] m-4 md:m-0 md:w-1/6 flex flex-col md:flex-row items-center md:justify-around md:justify-between">
      <div className="my-2 md:my-0">
        {createdBy}
      </div>
      <div className="my-2 md:my-0">
        {createdAt}
      </div>
    </div>

    <div className="flex flex-row space-x-2 w-fit">
      {
        canEdit &&
          <IconButton
              colorScheme={"teal"}
              aria-label='Action'
              fontSize='30px'
              rounded={15}
              icon={<FiEdit2 />}
              onClick={() => setOpenEditModal(true)}
          />
      }
      <ActionButton
        type={cancelType}
        action={cancelAction}
      />
      {
        accept && acceptAction &&
        <ActionButton
          type="ACCEPT"
          action={acceptAction}
        />
      }
    </div>
    {
      openEditModal &&
      <EntryUpdateModal
        isOpen={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          if(reload) reload();
        }}
        id={id} 
        quantity={quantity}
        title={`${product} (${um})`}
      />
    }
  </div>
}

export default Entry;